import * as React from 'react';
import { TreeSelect } from '../../TreeSelect/treeSelect';
import {
  Metadata,
  Schema,
  WorkspaceSetting,
  View,
  SchemaField
} from '../../../types';
import { NodeType } from '../../../enums/NodeType';
import { FilterProps } from '../filters';
import { ReferenceSearch } from '../../ReferenceSearch/referenceSearch';
import './referenceInput.scss';
import {
  excludeEmptyFilters,
  getIntersection,
  isBlanks,
  parseField
} from '../../../utils';
import {
  TreeFieldUtils,
  TreeSettings
} from '../../../utils/treeFieldUtils';
import { Blanks } from '../../../utils/constants';
import {
  getDefaultDataAPI,
  getUseCaseSearchAPI
} from '../../MetadataForm/ReferenceField/referenceField';
import { useSelector } from '../../../utils/useSelector';
import { API } from '../../../api';
import { Op } from '../../../enums/Op';
import { flatten } from 'rcd-cpp-workspace/utils/array';
import { ItemType } from '../../../enums/ItemType';

interface Props
  extends Pick<FilterProps, 'cache' | 'filters' | 'preappliedFilters'> {
  selected: (string | Metadata)[];
  nodeType?: NodeType | string;
  schemaField?: SchemaField;
  field?: string;
  onChange: (list: string[]) => void;
  inlineMenu?: boolean;
  singleValue?: boolean;
}

const getFilterByItemType = (
  filters: View.FiltersField[],
  preApplyFilters: View.FiltersField[]
) => {
  const allFilters = [preApplyFilters, filters];
  const itemTypes = Object.values(ItemType);
  for (const filters of allFilters) {
    if (!filters) continue;
    for (const f of filters) {
      if (f?.field == 'Item.itemType' && f.conditions?.length > 0) {
        for (const condition of f.conditions) {
          const isVals = condition[Op.is];
          const isNotVals = condition[Op.isNot];
          if (isVals?.length == 1) {
            return isVals[0];
          }
          if (isNotVals?.length == itemTypes.length - 1) {
            for (const itemType of itemTypes) {
              if (!isNotVals.includes(itemType)) return itemType;
            }
          }
        }
      }
    }
  }
  return null;
};

export const ReferenceInput = ({
  selected,
  nodeType,
  onChange,
  inlineMenu,
  singleValue,
  filters,
  preappliedFilters,
  schemaField,
  field,
  cache
}: Props) => {
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const schemas = useSelector(s => s.planning.selectedWorkspace.schemas);
  const workspaceSettings = useSelector(
    s => s.planning.selectedWorkspace.setting
  );
  const [useSeeAll, setUseSeeAll] = React.useState<any>(null);
  const { getTree } = getAPI();
  if (!nodeType) {
    const f = parseField(field, []);
    nodeType = f.schemaType;
  }
  const ts = React.useMemo(() => {
    const treeSetting = getTreeSettingForFilter(nodeType, schemas);
    if (singleValue) {
      treeSetting.settings.forEach(s => {
        s.depthLimit = 1;
      });
    }
    return treeSetting;
  }, [nodeType, schemas, singleValue]);
  const { shouldHide, seeOptions } = useSeeAll || {};
  const itemType = getFilterByItemType(filters, preappliedFilters);
  const refSearchSetting = React.useMemo(() => {
    if (nodeType != NodeType.UseCase) {
      return getDefaultDataAPI(nodeType, getAPI(), workspaceSettings, itemType);
    }
    return getUseCaseSearchAPI(getAPI(), workspaceSettings);
  }, [
    nodeType,
    workspaceSettings?.props?.showTSPUseCases,
    workspaceSettings,
    itemType
  ]);

  React.useEffect(() => {
    getUseSeeAll(
      ts,
      workspaceSettings,
      filters.filter(f => f.field != field),
      preappliedFilters,
      schemas,
      getAPI
    ).then(r => setUseSeeAll(r));
  }, [ts, workspaceSettings, filters, preappliedFilters, nodeType, schemas]);
  return (
    <>
      {!!ts && (
        <TreeSelect<Metadata>
          getData={async () => {
            const data = await getTree(ts, schemas, field);
            if (!singleValue) data.unshift(Blanks);
            return data;
          }}
          settings={ts.settings}
          selected={selected}
          seeOptions={seeOptions}
          isHidden={(data, parents, selected, ns) => {
            if (isBlanks(data)) return false;
            if (selected[data.props.refId]) return false;
            if (shouldHide && shouldHide(data, parents, selected, ns))
              return true;
            if (
              schemaField?.parentField == 'backtag' &&
              shouldHideBacktagData(data)
            ) {
              return true;
            }
            return TreeFieldUtils.isHidden(data);
          }}
          matches={(d, txt) =>
            (d?.props?.name || d?.props?.description || d?.props?.question)
              ?.toLowerCase()
              .indexOf(txt.toLowerCase()) >= 0
          }
          getUniqueKey={TreeFieldUtils.getUniqueKey}
          getChildren={d => d.metadata}
          onChange={(d, parents, checked) => {
            let list = (selected || []).filter(
              (v: any) =>
                v?.props?.refId != d.props?.refId && v != d.props?.refId
            );
            if (checked) {
              list.push(d);
            }
            onChange(
              list.map(l => (typeof l == 'string' ? l : l?.props?.refId))
            );
          }}
          inline
          renderData={TreeFieldUtils.renderData}
          highlightSearchText={true}
        />
      )}
      {!ts && (
        <ReferenceSearch<Metadata>
          inlineMenu={inlineMenu === false ? false : true}
          api={refSearchSetting?.api}
          seeOptions={refSearchSetting?.seeOptions}
          minLength={0}
          maxResults={50}
          open={inlineMenu === false ? undefined : true}
          cache={cache}
          multiple={!singleValue}
          includeInactives
          includeBlanks
          nodeType={nodeType as any}
          selected={selected}
          hideIcon={true}
          onChange={values => onChange(values?.map(v => v.props?.refId))}
          schemaField={schemaField}
        />
      )}
    </>
  );
};

const shouldHideBacktagData = (d: Metadata) => {
  if (!d.props?.id) return true;
  if (d.metadata?.length) {
    for (var m of d.metadata) {
      if (!shouldHideBacktagData(m)) return false;
    }
    return true;
  }
  return false;
};

const getFilterValues = (
  filters: View.FiltersField[],
  dataType: string,
  schemas: Schema[]
) => {
  if (!filters) return { refIds: [] };
  return filters.reduce(
    (acc, f) => {
      if (!f.conditions) return acc;
      const { refIds } = f.conditions.reduce<View.FilterCondition>(
        (values, c) => {
          if (c[Op.refIds] && c[Op.refIds].length) {
            values.refIds = values.refIds.concat(c[Op.refIds]);
          }
          return values;
        },
        { refIds: [] }
      );
      if (refIds.length) {
        const { field, schema, referenceSchema } = parseField(f.field, schemas);
        if (
          (referenceSchema != null &&
            referenceSchema.props?.type == dataType) ||
          (schema.props?.displayField == field && schema.props.type == dataType)
        ) {
          if (acc.refIds.length) {
            acc.refIds = getIntersection(acc.refIds, refIds);
          } else {
            acc.refIds = refIds;
          }
        }
      }
      return acc;
    },
    { refIds: [] }
  );
};

const isDecendantOf = (decendantRefIds: string[], node: Metadata) => {
  if (!node.metadata?.length) return false;
  for (const m of node.metadata) {
    if (decendantRefIds.includes(m.props?.refId)) return true;
    if (isDecendantOf(decendantRefIds, m)) return true;
  }
  return false;
};

const getUseSeeAll = async (
  ts: TreeSettings,
  workspaceSettings: WorkspaceSetting,
  filters: View.FiltersField[] = [],
  preappliedFilters: View.FiltersField[] = [],
  schemas: Schema[],
  getAPI: () => API
) => {
  if (!ts?.settings) return null;
  let limitedData = [];
  if (filters.length || preappliedFilters.length) {
    const { getTree } = getAPI();
    const tree = await getTree(ts);
    if (!tree?.length) return null;
    const flattenTree = flatten(tree, n => n.metadata);
    const allFilters = []
      .concat(excludeEmptyFilters(filters))
      .concat(preappliedFilters);
    const limitedMap = {} as { [x: string]: string[] };
    ts.settings.forEach(s => {
      const { refIds } = getFilterValues(allFilters, s.nodeType, schemas);
      const parentTypes = Object.keys(limitedMap);
      if (refIds.length && parentTypes.length) {
        parentTypes.forEach((pt: NodeType) => {
          const parentRefIds = flattenTree
            .filter(t => t.labels?.includes(pt) && isDecendantOf(refIds, t))
            .map(t => t.props.refId);
          if (parentRefIds.length) {
            if (!limitedMap[pt].length) {
              limitedMap[pt] = parentRefIds;
            } else {
              limitedMap[pt] = getIntersection(parentRefIds, limitedMap[pt]);
            }
          }
        });
      }
      limitedMap[s.nodeType] = refIds;
    });
    limitedData = Object.values(limitedMap).reduce(
      (acc, v) => acc.concat(v),
      []
    );
    if (!limitedData.length) limitedData = null;
  }
  const r = TreeFieldUtils.useSeeAll(
    workspaceSettings,
    ts?.settings,
    null,
    limitedData
  );
  if (!limitedData) return r;
  return {
    ...r,
    shouldHide: (d, p, s, ns) => {
      const ss = { ...s };
      limitedData?.forEach(k => {
        ss[k] = true;
      });
      return r.shouldHide(d, p, ss, ns);
    }
  };
};

export const getTreeSettingForFilter = (dataType: any, schemas: Schema[]) => {
  const ts = TreeFieldUtils.getTreeSetting(dataType, null, schemas);
  if (!ts?.settings?.length) return null;
  return {
    ...ts,
    settings: ts.settings.map(
      ({ hideCheckbox, sort, displayField, nodeType }) => ({
        hideCheckbox: d => (isBlanks(d) ? false : hideCheckbox),
        sort,
        displayField,
        nodeType
      })
    )
  } as TreeSettings;
};
